import { Object } from '@vae/server/dist/client'
import { ConsumptionChartData } from '@vae/server/src/utils/arrays/types/ChartData'
import { GroupingOptionsV2 } from '@vae/server/src/utils/arrays/types/GroupingOptions'
import { TFunction } from 'i18next'
import { TooltipData } from '../../../components/baseTooltip/BaseTooltip'
import { ChartModes } from '../types/types'

interface TooltipParams {
	charts: ConsumptionChartData
	activeElement: number
	chartModes: ChartModes
	object: Object
	t: TFunction<'pages_electricity', undefined>
	timespan: GroupingOptionsV2
	isDarkMode: boolean
}

const getConsumptionElement = ({
	charts,
	activeElement,
	chartModes,
	t,
	isDarkMode,
}: TooltipParams): TooltipData => {
	const value = charts.consumption.groupedData[activeElement]?.kwh ?? 0

	const label = chartModes.production ? t('nettokulutus') : t('kulutus')
	return { color: isDarkMode ? '#3cb49f' : '#52ae32', label, value }
}

const getExchangePriceElement = ({
	charts,
	chartModes,
	activeElement,
	t,
	timespan,
}: TooltipParams): TooltipData | null => {
	if (!charts.exchangePricesWithMarginalGrouped) return null
	let value = 0

	if (timespan === 'day') {
		value = charts.exchangePricesWithMarginalGrouped.groupedData[activeElement]?.priceWithAlv ?? 0
	} else {
		let totalPrice = 0
		if (chartModes.exchangePrices) {
			totalPrice = charts?.consumptionWithExchangePrice.groupedData[activeElement]?.value ?? 0
		}

		const totalConsumption = charts.consumption.groupedData[activeElement]?.kwh ?? 0

		value = totalConsumption > 0 ? totalPrice / totalConsumption : 0
	}

	return {
		color: '#ffb30d',
		label: t('hinta'),
		value,
		overrideUnit: t('centsPerKWh'),
	}
}

const getProductionElement = ({
	charts,
	activeElement,
	t,
	isDarkMode,
}: TooltipParams): TooltipData | null => {
	if (!charts.production) return null

	return {
		color: isDarkMode ? '#52ae32' : '#127d27',
		label: t('tuotanto'),
		value: charts.production.data[activeElement]?.value ?? 0,
	}
}

const getTransferPriceElements = (params: TooltipParams): TooltipData[] => {
	const { charts, activeElement, chartModes, t } = params
	const elements: TooltipData[] = []

	if (chartModes.exchangePrices) {
		const totalPrice =
			charts?.transferPrices.data.grouped.costWithElectricityPrice.groupedData[activeElement]
				?.value ?? 0
		const totalConsumption = charts.consumption.groupedData[activeElement]?.kwh ?? 0

		let value = 0

		value = totalConsumption > 0 ? totalPrice / totalConsumption : 0
		const overrideUnit = value > 100 ? '€' : t('centsPerKWh')
		if (value > 100) value /= 100

		elements.push({ color: '#155f65', label: t('summedTransfer'), value, overrideUnit })
	} else {
		const transferPrice =
			charts.transferPrices.data.grouped.costWithoutElectricityPrice.groupedData[activeElement]
				?.value ?? 0
		const totalConsumption = charts.consumption.groupedData[activeElement]?.kwh ?? 0

		let value = totalConsumption > 0 ? transferPrice / totalConsumption : 0

		const overrideUnit = value > 100 ? '€' : t('centsPerKWh')
		if (value > 100) value /= 100
		elements.push({ color: '#155f65', label: t('transfer'), value, overrideUnit })
	}

	return elements
}

const getTotalElement = (params: TooltipParams): TooltipData | null => {
	const { charts, activeElement, chartModes } = params
	if (!chartModes.transferPrices && !chartModes.exchangePrices) return null

	let value = 0
	let overrideUnit = params.t('cents')

	if (chartModes.exchangePrices && !chartModes.transferPrices) {
		value = charts.consumptionWithExchangePrice.groupedData[activeElement]?.value ?? 0
	} else if (chartModes.transferPrices && !chartModes.exchangePrices && !chartModes.production) {
		value =
			charts.transferPrices.data.grouped.costWithoutElectricityPrice.groupedData[activeElement]
				?.value ?? 0
	} else if (chartModes.transferPrices) {
		value =
			charts.transferPrices.data.grouped.costWithElectricityPrice.groupedData[activeElement]
				?.value ?? 0
	}

	if (chartModes.exchangePrices && chartModes.transferPrices) {
		value =
			charts.transferPrices.data.grouped.costWithElectricityPrice.groupedData[activeElement]
				?.value ?? 0
	}

	if (value > 100) {
		value /= 100
		overrideUnit = '€'
	}

	return { color: '#c3c3c3', label: params.t('total'), value, overrideUnit }
}

const getConsumptionTooltipElements = (
	charts: ConsumptionChartData,
	activeElement: number,
	chartModes: ChartModes,
	object: Object,
	t: TFunction<'pages_electricity', undefined>,
	timespan: GroupingOptionsV2,
	isDarkMode: boolean
): TooltipData[] => {
	const params: TooltipParams = {
		charts,
		activeElement,
		chartModes,
		object,
		t,
		timespan,
		isDarkMode,
	}
	const elements: TooltipData[] = []

	elements.push(getConsumptionElement(params))

	if (chartModes.production) {
		const productionElement = getProductionElement(params)
		if (productionElement) elements.push(productionElement)
	}

	if (chartModes.exchangePrices) {
		const exchangeElement = getExchangePriceElement(params)
		if (exchangeElement) elements.push(exchangeElement)
	}

	if (chartModes.transferPrices) {
		elements.push(...getTransferPriceElements(params))
	}

	const totalElement = getTotalElement(params)
	if (totalElement) elements.push(totalElement)

	return elements
}

export default getConsumptionTooltipElements
